import React, { useEffect, useState, useMemo } from "react";
import { Bus, Clock, MapPin, Activity, Warehouse, Route, ChevronDown, Info, Battery, BatteryWarning, Zap } from "lucide-react";
import { api, fmtDec, fmtInt } from "../lib/api";

const COLOR_IDA   = "#0EA5E9";
const COLOR_VUELTA = "#A855F7";
const COLOR_DEAD_COCHERA = "#94A3B8";
const COLOR_DEAD_INTER   = "#FBBF24";
const COLOR_SOC          = "#10B981";   // SoC line
const COLOR_SOC_CRITICAL = "#EF4444";   // SoC bajo umbral

export default function Planificacion() {
  const [lineas, setLineas] = useState([]);
  const [codigo, setCodigo] = useState(null);
  const [combinaciones, setCombinaciones] = useState([]);
  const [tipoDia, setTipoDia] = useState(null);
  const [temporada, setTemporada] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  // Parámetros de batería
  const [bateriaKwh, setBateriaKwh] = useState("");  // vacío = auto por tamaño
  const [cargaOportunidadKw, setCargaOportunidadKw] = useState(0);
  const [minRegMin, setMinRegMin] = useState(10);
  const [socInicial, setSocInicial] = useState(100);

  useEffect(() => {
    api.get("/lineas").then((r) => {
      const ls = r.data.sort((a, b) => a.lote.localeCompare(b.lote) || a.numero_linea.localeCompare(b.numero_linea));
      setLineas(ls);
      if (ls.length && !codigo) setCodigo(ls[0].codigo_linea);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!codigo) return;
    setCombinaciones([]); setTipoDia(null); setTemporada(null); setPlan(null);
    api.get(`/planificacion/${encodeURIComponent(codigo)}/combinaciones`).then((r) => {
      const cs = r.data.combinaciones || [];
      setCombinaciones(cs);
      if (cs.length) {
        setTipoDia(cs[0].tipo_dia);
        setTemporada(cs[0].temporada_raw);
      }
    });
  }, [codigo]);

  useEffect(() => {
    if (!codigo || !tipoDia || !temporada) return;
    setLoading(true);
    api
      .get(`/planificacion/${encodeURIComponent(codigo)}`, {
        params: {
          tipo_dia: tipoDia, temporada_raw: temporada,
          soc_inicial: socInicial,
          bateria_kwh: bateriaKwh || undefined,
          carga_oportunidad_kw: cargaOportunidadKw,
          min_regulacion_carga_min: minRegMin,
        },
      })
      .then((r) => setPlan(r.data))
      .catch(() => setPlan(null))
      .finally(() => setLoading(false));
  }, [codigo, tipoDia, temporada, socInicial, bateriaKwh, cargaOportunidadKw, minRegMin]);

  // Min/max para el eje X (con margen)
  const { startMin, endMin } = useMemo(() => {
    if (!plan?.bloques?.length) return { startMin: 5 * 60, endMin: 24 * 60 };
    const starts = plan.bloques.flatMap((b) => [b.start_min, ...b.deadheads.map((d) => d.start_min)]);
    const ends = plan.bloques.flatMap((b) => [b.end_min, ...b.deadheads.map((d) => d.end_min)]);
    return {
      startMin: Math.max(0, Math.min(...starts) - 15),
      endMin: Math.min(36 * 60, Math.max(...ends) + 15),
    };
  }, [plan]);

  const totalMin = Math.max(60, endMin - startMin);
  const hourTicks = [];
  for (let h = Math.ceil(startMin / 60); h * 60 <= endMin; h++) hourTicks.push(h);

  const currentLinea = lineas.find((l) => l.codigo_linea === codigo);
  const temporadasUnique = [...new Set(combinaciones.filter((c) => c.tipo_dia === tipoDia).map((c) => c.temporada_raw))];
  const tiposDiaUnique = [...new Set(combinaciones.map((c) => c.tipo_dia))];

  return (
    <div className="space-y-6" data-testid="planificacion-page">
      <div>
        <div className="text-xs font-semibold tracking-widest text-gray-500 uppercase">Planificación de servicio</div>
        <h1 className="section-title mt-1">Timeline de vehículos · carga, encadenamiento y dead-mileage</h1>
        <p className="section-sub">
          Asignación greedy de expediciones a vehículos, con regulaciones y km en vacío desde la cochera. Permite estimar la flota
          necesaria por línea, sublínea, tipo de día y temporada, y ver la carga real de cada bus.
        </p>
      </div>

      {/* Selectores */}
      <div className="crtm-card p-4 grid grid-cols-1 md:grid-cols-3 gap-3">
        <SelectField label="Línea" testId="sel-linea">
          <select
            value={codigo || ""}
            onChange={(e) => setCodigo(e.target.value)}
            data-testid="sel-linea-input"
          >
            {lineas.map((l) => (
              <option key={l.codigo_linea} value={l.codigo_linea}>
                {l.lote} · Línea {l.numero_linea} — {l.denominacion?.slice(0, 50)}
              </option>
            ))}
          </select>
        </SelectField>
        <SelectField label="Tipo de día" testId="sel-tipodia">
          <select
            value={tipoDia || ""}
            onChange={(e) => setTipoDia(e.target.value)}
            data-testid="sel-tipodia-input"
            disabled={!tiposDiaUnique.length}
          >
            {tiposDiaUnique.map((td) => (
              <option key={td} value={td}>{td}</option>
            ))}
          </select>
        </SelectField>
        <SelectField label="Temporada" testId="sel-temporada">
          <select
            value={temporada || ""}
            onChange={(e) => setTemporada(e.target.value)}
            data-testid="sel-temporada-input"
            disabled={!temporadasUnique.length}
          >
            {temporadasUnique.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </SelectField>
      </div>

      {/* Parámetros de batería */}
      <div className="crtm-card p-4" data-testid="bateria-controls">
        <div className="flex items-center gap-2 mb-3">
          <Battery size={15} className="text-emerald-700" />
          <h3 className="font-heading font-semibold text-[14px]">Simulación de batería</h3>
          {plan?.bateria && (
            <span className="text-xs text-gray-500 ml-auto">
              Capacidad asumida: <strong className="text-gray-800">{plan.bateria.capacidad_kwh} kWh</strong> ·
              Factor consumo: <strong className="text-gray-800">{plan.bateria.factor_kwh_km} kWh/km</strong>
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-xs">
          <NumberField
            label="SoC inicial (%)" testId="soc-inicial"
            value={socInicial} min={50} max={100} step={5}
            onChange={setSocInicial}
            hint="Estado de carga al salir de cochera"
          />
          <NumberField
            label="Capacidad batería (kWh)" testId="bateria-kwh"
            value={bateriaKwh} placeholder="auto por tamaño" min={100} max={800} step={25}
            onChange={(v) => setBateriaKwh(v)}
            hint={`Auto: 8m=250 / 10m=300 / 12m=400 / 15m=500 / 18m=600 kWh`}
          />
          <NumberField
            label="Carga oportunidad (kW)" testId="carga-oportunidad"
            value={cargaOportunidadKw} min={0} max={600} step={50}
            onChange={setCargaOportunidadKw}
            hint="0 = sin pantógrafo en cabecera. Típico: 200-450 kW"
          />
          <NumberField
            label="Regulación mínima (min)" testId="min-regulacion"
            value={minRegMin} min={3} max={30} step={1}
            onChange={setMinRegMin}
            hint="Tiempo mínimo de regulación para activar la carga"
          />
        </div>
      </div>

      {!loading && plan && plan.n_expediciones === 0 && (
        <div className="crtm-card p-6 text-sm text-amber-700" data-testid="empty">
          No hay expediciones para esta combinación de tipo de día + temporada.
        </div>
      )}

      {!loading && plan && plan.n_expediciones > 0 && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-7 gap-3" data-testid="kpis">
            <Kpi icon={Bus}      label="Vehículos necesarios" value={plan.n_vehiculos} tone="emerald" />
            <Kpi icon={Activity} label="Expediciones"          value={plan.n_expediciones} tone="blue" />
            <Kpi icon={Route}    label="Km comerciales"        value={`${fmtInt(plan.kpis.km_comercial)} km`} tone="green" />
            <Kpi icon={Warehouse} label="Km en vacío"          value={`${fmtInt(plan.kpis.km_vacio)} km`} tone="amber" />
            <Kpi icon={Clock}    label="Utilización media"     value={`${plan.kpis.utilizacion_pct}%`} tone="blue" />
            <Kpi icon={Info}     label="% vacío sobre total"   value={`${plan.kpis.ratio_vacio_pct}%`} tone="gray" />
            <Kpi
              icon={(plan.bateria?.n_vehiculos_criticos || 0) > 0 ? BatteryWarning : Battery}
              label="SoC mínimo"
              value={`${plan.bateria?.soc_min_global_pct ?? "—"}%`}
              tone={
                (plan.bateria?.soc_min_global_pct ?? 100) < 20 ? "amber" :
                (plan.bateria?.soc_min_global_pct ?? 100) < 40 ? "blue" : "emerald"
              }
            />
          </div>

          {plan.cochera && (
            <div className="text-xs text-gray-500 flex items-center gap-1.5" data-testid="cochera-info">
              <Warehouse size={13} /> Cochera asignada: <strong className="text-gray-800">{plan.cochera.nombre}</strong>
              <span className="text-gray-400">({plan.cochera.lat?.toFixed(4)}, {plan.cochera.lon?.toFixed(4)})</span>
            </div>
          )}

          {/* Timeline */}
          <div className="crtm-card overflow-hidden" data-testid="timeline-card">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-[15px]">
                Timeline · Línea {plan.numero_linea} · {plan.tipo_dia} · {plan.temporada_raw}
              </h3>
              <Legend />
            </div>
            <div className="overflow-x-auto">
              <div className="min-w-[900px]" style={{ position: "relative" }}>
                {/* Ruler */}
                <div className="flex border-b border-gray-100 sticky top-0 bg-white z-10" style={{ paddingLeft: 110 }}>
                  {hourTicks.map((h) => (
                    <div
                      key={h}
                      style={{
                        flex: `0 0 ${((60) / totalMin) * 100}%`,
                        textAlign: "left",
                      }}
                      className="text-[11px] text-gray-500 py-2 font-mono border-l border-gray-100 px-1"
                    >
                      {String(h % 24).padStart(2, "0")}:00
                    </div>
                  ))}
                </div>

                {/* Filas por vehículo */}
                {plan.bloques.map((bus, idx) => (
                  <div
                    key={bus.vehiculo}
                    className={"flex items-center border-b border-gray-50 group hover:bg-gray-50/60 " + (idx % 2 === 1 ? "bg-gray-50/30" : "")}
                    data-testid={`row-${bus.vehiculo}`}
                  >
                    <div className="flex-shrink-0 w-[110px] py-2.5 px-3 border-r border-gray-100">
                      <div className="font-mono font-bold text-sm text-gray-900 flex items-center gap-1">
                        <Bus size={13} className="text-emerald-700" /> {bus.vehiculo}
                        {bus.bateria_critica && <BatteryWarning size={12} className="text-red-600" title="SoC < 20%" />}
                      </div>
                      <div className="text-[10px] text-gray-500 font-mono">
                        {bus.n_trips} trips · {fmtInt(bus.km_comercial)}+{fmtInt(bus.km_vacio)} km
                      </div>
                      <div className="text-[10px] text-gray-400 font-mono">util {bus.utilizacion_pct}%</div>
                      <div className={`text-[10px] font-mono font-bold ${bus.bateria_critica ? "text-red-600" : bus.soc_min_pct < 40 ? "text-amber-700" : "text-emerald-700"}`}>
                        SoC {bus.soc_min_pct}% (min)
                      </div>
                    </div>
                    <div className="flex-1 relative h-14">
                      {/* deadheads first (debajo) */}
                      {bus.deadheads.map((dh, i) => {
                        const left = ((dh.start_min - startMin) / totalMin) * 100;
                        const width = ((dh.end_min - dh.start_min) / totalMin) * 100;
                        if (width <= 0) return null;
                        const color = dh.tipo.startsWith("cochera") ? COLOR_DEAD_COCHERA : COLOR_DEAD_INTER;
                        return (
                          <div
                            key={`dh-${i}`}
                            className="absolute h-2.5 rounded-sm"
                            style={{
                              left: `${left}%`, width: `${width}%`, top: "44%",
                              background: `repeating-linear-gradient(45deg, ${color}, ${color} 4px, ${color}99 4px, ${color}99 8px)`,
                            }}
                            title={`${dh.tipo} · ${dh.desde} → ${dh.hasta} · ${dh.km} km (${dh.start_label}-${dh.end_label})`}
                          />
                        );
                      })}
                      {/* trips */}
                      {bus.trips.map((t, i) => {
                        const left = ((t.salida_min - startMin) / totalMin) * 100;
                        const width = ((t.llegada_min - t.salida_min) / totalMin) * 100;
                        if (width <= 0) return null;
                        const color = t.sentido === "Ida" ? COLOR_IDA : COLOR_VUELTA;
                        return (
                          <div
                            key={`t-${i}`}
                            className="absolute rounded-sm flex items-center justify-center text-[9px] text-white font-mono font-bold border border-white/60 overflow-hidden whitespace-nowrap"
                            style={{
                              left: `${left}%`, width: `${width}%`, top: "8%", height: "32%",
                              backgroundColor: color, minWidth: 4,
                            }}
                            title={`${t.sentido} ${t.salida_label}-${t.llegada_label} · itin ${t.itinerario} · ${t.veh_km} km`}
                          >
                            {width > 2 && (t.sentido === "Ida" ? "→" : "←")}
                          </div>
                        );
                      })}
                      {/* SoC line (svg overlay) */}
                      <SocOverlay series={bus.soc_series} startMin={startMin} totalMin={totalMin} critico={bus.bateria_critica} />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Resumen tabular */}
            <div className="overflow-x-auto border-t border-gray-100">
              <table className="crtm-table" data-testid="resumen-table">
                <thead>
                  <tr>
                    <th>Vehículo</th>
                    <th>Servicio</th>
                    <th className="text-right">Trips</th>
                    <th className="text-right">Km comerciales</th>
                    <th className="text-right">Km vacío</th>
                    <th className="text-right">Horas servicio</th>
                    <th className="text-right">Horas totales</th>
                    <th className="text-right">Utilización</th>
                    <th className="text-right">kWh consumidos</th>
                    <th className="text-right">SoC min → fin</th>
                  </tr>
                </thead>
                <tbody>
                  {plan.bloques.map((b) => (
                    <tr key={b.vehiculo} className={b.bateria_critica ? "bg-red-50/60" : ""}>
                      <td className="font-mono font-bold">
                        {b.vehiculo}
                        {b.bateria_critica && <BatteryWarning size={11} className="inline ml-1 text-red-600" />}
                      </td>
                      <td className="font-mono text-xs">{b.start_label} → {b.end_label}</td>
                      <td className="text-right font-mono">{b.n_trips}</td>
                      <td className="text-right font-mono">{fmtInt(b.km_comercial)}</td>
                      <td className="text-right font-mono text-amber-700">{fmtInt(b.km_vacio)}</td>
                      <td className="text-right font-mono">{fmtDec(b.horas_servicio, 1)} h</td>
                      <td className="text-right font-mono">{fmtDec(b.horas_total, 1)} h</td>
                      <td className="text-right font-mono">
                        <span className={b.utilizacion_pct >= 60 ? "text-green-700 font-semibold" : b.utilizacion_pct >= 40 ? "text-amber-700" : "text-red-700"}>
                          {b.utilizacion_pct}%
                        </span>
                      </td>
                      <td className="text-right font-mono">{fmtInt(b.kwh_total_consumido)} kWh</td>
                      <td className="text-right font-mono">
                        <span className={b.bateria_critica ? "text-red-700 font-semibold" : b.soc_min_pct < 40 ? "text-amber-700" : "text-emerald-700 font-semibold"}>
                          {b.soc_min_pct}% → {b.soc_final_pct}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[11px] text-gray-500 flex gap-2 items-start">
            <Info size={12} className="text-gray-400 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Algoritmo:</strong> asignación greedy. Cada expedición se asigna al primer vehículo con suficiente layover ({" "}
              <strong>≥ 8 min</strong>) y, si el extremo no coincide, se añade un trayecto en vacío entre cabeceras a 35 km/h.
              Los trayectos desde / hacia cochera se calculan por distancia haversine (origen de coordenadas: cochera asignada al lote).
            </span>
          </div>
        </>
      )}
    </div>
  );
}

const SelectField = ({ label, children, testId }) => (
  <div data-testid={testId}>
    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{label}</div>
    <div className="relative">
      {children}
      <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
    </div>
    <style>{`
      [data-testid="${testId}"] select {
        width: 100%;
        padding: 8px 30px 8px 10px;
        border: 1px solid #E5E7EB;
        border-radius: 6px;
        background: white;
        font-size: 13px;
        appearance: none;
      }
      [data-testid="${testId}"] select:focus { outline: none; border-color: #14532D; }
    `}</style>
  </div>
);

const Kpi = ({ icon: Icon, label, value, tone = "gray" }) => {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-100",
    blue:    "bg-blue-50 text-blue-700 border-blue-100",
    green:   "bg-green-50 text-green-700 border-green-100",
    amber:   "bg-amber-50 text-amber-700 border-amber-100",
    gray:    "bg-gray-50 text-gray-700 border-gray-100",
  };
  return (
    <div className={`crtm-card p-3 border ${toneMap[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase font-bold tracking-widest opacity-80">
        <Icon size={12} /> {label}
      </div>
      <div className="text-lg font-bold mt-1 text-gray-900">{value}</div>
    </div>
  );
};

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-[10px] text-gray-600">
    <LegendItem color={COLOR_IDA}      label="Ida" />
    <LegendItem color={COLOR_VUELTA}   label="Vuelta" />
    <LegendItem color={COLOR_DEAD_COCHERA} label="Cochera ↔ línea" stripes />
    <LegendItem color={COLOR_DEAD_INTER}    label="Interlinking" stripes />
    <LegendItem color={COLOR_SOC} label="SoC %" line />
  </div>
);

const LegendItem = ({ color, label, stripes, line }) => (
  <div className="flex items-center gap-1">
    {line ? (
      <svg width="18" height="10" viewBox="0 0 18 10">
        <polyline points="0,7 6,3 12,5 18,2" fill="none" stroke={color} strokeWidth="1.5" />
      </svg>
    ) : (
      <span
        className="inline-block w-4 h-3 rounded-sm"
        style={{
          background: stripes
            ? `repeating-linear-gradient(45deg, ${color}, ${color} 3px, ${color}99 3px, ${color}99 6px)`
            : color,
        }}
      />
    )}
    {label}
  </div>
);

/**
 * Línea de SoC superpuesta sobre la fila del bus.
 * El eje Y va de 0% (abajo) a 100% (arriba). La línea SoC se dibuja en svg.
 */
const SocOverlay = ({ series, startMin, totalMin, critico }) => {
  if (!series?.length) return null;
  const minSoc = Math.min(...series.map((s) => s.soc));
  // Construir el path: x = ((t-startMin)/totalMin)*100, y = (100 - soc)
  // Aprovechamos viewBox 100x100 con preserveAspectRatio none
  const points = series.map((s) => {
    const x = ((s.t - startMin) / totalMin) * 100;
    const y = 100 - s.soc; // 0% en bottom
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const stroke = critico ? COLOR_SOC_CRITICAL : COLOR_SOC;
  // Crear área sombreada bajo la línea para mayor visibilidad
  const areaPoints = `${points[0].split(",")[0]},100 ${points.join(" ")} ${points[points.length-1].split(",")[0]},100`;
  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      width="100%" height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
    >
      {/* gridline 50% */}
      <line x1="0" y1="50" x2="100" y2="50" stroke="#E5E7EB" strokeWidth="0.3" strokeDasharray="1 1" />
      {/* gridline 20% (umbral crítico) */}
      <line x1="0" y1="80" x2="100" y2="80" stroke="#F87171" strokeWidth="0.3" strokeDasharray="1 1" />
      <polygon points={areaPoints} fill={stroke} fillOpacity="0.08" />
      <polyline points={points.join(" ")} fill="none" stroke={stroke} strokeWidth="1.2" vectorEffect="non-scaling-stroke" />
      {/* etiqueta min */}
      <text
        x="98" y="3" textAnchor="end"
        fontSize="6" fill={stroke} fontFamily="monospace" fontWeight="bold"
      >
        SoC min {minSoc.toFixed(0)}%
      </text>
    </svg>
  );
};

const NumberField = ({ label, value, onChange, min, max, step, hint, placeholder, testId }) => (
  <div data-testid={testId}>
    <div className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider mb-1">{label}</div>
    <input
      type="number"
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      min={min} max={max} step={step}
      placeholder={placeholder || ""}
      className="w-full px-2 py-1.5 border border-gray-200 rounded-md font-mono text-sm focus:outline-none focus:border-emerald-700"
    />
    {hint && <div className="text-[10px] text-gray-400 mt-1 leading-tight">{hint}</div>}
  </div>
);
